// Army stacks on the flat strategic map: an ink tank seen from above, in the holder's colour, in
// place of the lit 3D models (whose shadows read as foreign objects on the pastel ground). Drawn in
// the style of overlays/strategicIcons.ts: a coloured body with a one-pixel ink outline.
//
//   stack ─► one icon per side (placeIcons) ─► one flat quad each, lying on the ground
//
//          barrel │       forward (+z)
//        ┌────────┴──┐       ▲
//        │   (  )    │       │      an icon is a symbol of "forces on this line", like the models:
//        └───────────┘       │      never a count or a strength
//
// Same truth as the models: a stack exists only on a sourced front, and units.ts keeps its front and
// sources whatever it draws.

import * as THREE from 'three/webgpu';
import { abs, attribute, float, fwidth, length, max, min, mix, positionGeometry, smoothstep, vec2, vec4 } from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import type { HexData } from '../hexdata.ts';
import { INK } from '../overlays/parchment.ts';
import { rgb, screen, screenNode } from '../tsl/color.ts';
import { MEMBERS, PER_SCREEN, placeStack, type UnitStack } from './stacks.ts';

type F = Node<'float'>;
type V2 = Node<'vec2'>;

/** An icon in the world: where it stands, which way it faces, whose colour it wears. */
export interface Icon {
  x: number;
  z: number;
  yaw: number;
  actor: number;
}

/** Icons the mesh holds: both sides of every stack the screen has room for. */
export const ICON_CAPACITY = PER_SCREEN * 2;

/** Half the quad's side, in world units per unit of model scale. */
export const ICON_HALF = 0.2;

/** Actor index for "no colour known" (units.ts NO_ACTOR). */
const NO_ACTOR = 0;
const UNKNOWN_FILL = '#c9c1b0';
const STROKE_PX = 1.15;
const STROKE_MIN = 0.05;

// The tank in quad coordinates (x right, z forward, the quad spans ±1).
const HULL_HALF: readonly [number, number] = [0.62, 0.72];
const HULL_AT: readonly [number, number] = [0, -0.08];
const TURRET_AT: readonly [number, number] = [0, -0.1];
const TURRET_R = 0.36;
const BARREL_HALF: readonly [number, number] = [0.07, 0.4];
const BARREL_AT: readonly [number, number] = [0, 0.58];

/**
 * The icons of a stack: the centre of each side's cluster, facing as the cluster does. One where no
 * foe is known, two facing each other across the line otherwise.
 */
export function placeIcons(stack: UnitStack, scale: number): Icon[] {
  const models = placeStack(stack, scale);
  const icons: Icon[] = [];

  for (let at = 0; at < models.length; at += MEMBERS.length) {
    const side = models.slice(at, at + MEMBERS.length);
    icons.push({
      x: side.reduce((sum, m) => sum + m.x, 0) / side.length,
      z: side.reduce((sum, m) => sum + m.z, 0) / side.length,
      yaw: side[0].yaw,
      actor: side[0].actor,
    });
  }

  return icons;
}

/** Signed distance to an axis-aligned box. */
function box(p: V2, at: readonly [number, number], half: readonly [number, number]): F {
  const d = abs(p.sub(vec2(...at))).sub(vec2(...half));
  return length(max(d, 0)).add(min(max(d.x, d.y), 0));
}

function disc(p: V2, at: readonly [number, number], r: number): F {
  return length(p.sub(vec2(...at))).sub(r);
}

/** Coverage of a shape from its signed distance (negative inside). */
function inside(d: F, px: F): F {
  return smoothstep(px, px.negate(), d);
}

/** Coverage of an outline `abs(d)` wide. */
function outline(d: F, px: F): F {
  const half = px.mul(STROKE_PX).max(STROKE_MIN);
  return smoothstep(half.add(px), half, d.abs());
}

/** What one draw of the icons needs. */
export interface IconSlot {
  mesh: THREE.InstancedMesh;
  actor: THREE.InstancedBufferAttribute;
}

/** The mesh of icons: a flat quad per instance, transformed by units.ts, coloured by instance actor. */
export function iconSlot(data: HexData): IconSlot {
  const geometry = new THREE.PlaneGeometry(2, 2);
  geometry.rotateX(-Math.PI / 2);

  const actor = new THREE.InstancedBufferAttribute(new Float32Array(ICON_CAPACITY), 1);
  actor.setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute('iActor', actor);

  const mesh = new THREE.InstancedMesh(geometry, material(data), ICON_CAPACITY);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.count = 0;
  mesh.visible = false;
  mesh.frustumCulled = false;
  mesh.castShadow = false;
  mesh.receiveShadow = false;
  mesh.name = 'political:unitIcons';

  return { mesh, actor };
}

/** Unlit and transparent at the edges; lifted a little so ground relief under it does not cut it. */
function material(data: HexData): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false });
  m.polygonOffset = true;
  m.polygonOffsetFactor = -2;
  m.polygonOffsetUnits = -2;

  // The quad's own coordinates: instancing moves positionLocal to the instance's place in the world.
  const p = positionGeometry.xz;
  const px = fwidth(p).length();
  const hull = box(p, HULL_AT, HULL_HALF);
  const turret = disc(p, TURRET_AT, TURRET_R);
  const barrel = box(p, BARREL_AT, BARREL_HALF);

  const body = max(inside(hull, px), inside(turret, px));
  const ink = max(max(outline(hull, px), outline(turret, px)), inside(barrel, px));

  // Rounded: the varying interpolates a hair off the integer index and would flicker between actors.
  const actor = (attribute('iActor', 'float') as unknown as F).round();
  const fill = mix(screen(UNKNOWN_FILL), screenNode(data.actorColor(actor, 0)), actor.greaterThan(NO_ACTOR).select(float(1), float(0)));

  m.colorNode = vec4(mix(fill, rgb(INK), ink), max(body, ink));
  return m;
}
